/**
 * `/queue` — TASK 9.1 Steps 1-3 (US-7: AC 7.1 / 7.2 / 7.4 / 7.6).
 *
 * The mock's `_queue(V,q)` view model (L1105-1112) and template (L574-608),
 * wired to the real queue API (TASK 6.3) instead of `state.queue`. The shape
 * survives — NOW PLAYING card, QUEUE|HISTORY tabs, Save/Clear actions, drag
 * rows with ↑/↓/✕/⋯, "Show more (N more)" at +60 — plus what the mock lacked:
 *
 *   mock-defect fix 5 — move-to-position-N via ./MoveToPositionSheet.js, on
 *       the per-row ⋯ sheet next to top/bottom/remove. This (with ↑/↓) is the
 *       keyboard and screen-reader reorder path AC 7.2 requires; drag is the
 *       pointer convenience, never the only way.
 *   AC 7.2 auto-scroll — pointer-drag near a vertical viewport edge scrolls,
 *       so a long queue is reorderable by drag beyond one screen.
 *   AC 7.6 — loading skeleton, retriable error and the QUEUE'S DRY empty
 *       panel all render; the panel is never blank.
 *
 * ── THE API'S POSITION SPACE (every reorder flows through this) ───────────
 * Server positions are 0-based over the WHOLE queue: the currently-playing
 * item sits at `playingIndex(currentIndex, totalItems)` and anything a past
 * `setCurrentIndex` left behind sits BEFORE it. The UI shows only the playing
 * item + the UPCOMING slice (mock `up = queue.slice(1)`), numbered from 1. So:
 *
 *   display slot N        -> absolute `playing + N` (MoveToPositionSheet.js
 *                            `moveToPositionDirective`, unit-pinned);
 *   "Move to top"         -> `{kind:'to', position: playing + 1}` — NEVER the
 *                            API's `{kind:'top'}`, which is absolute 0 and
 *                            would shove the item ABOVE the playing track,
 *                            where the queue screen cannot even show it;
 *   "Move up" on slot 1   -> a client-side no-op for the same reason;
 *   "Move to bottom"/down -> the API's own directives (clamping at the end is
 *                            exactly what the user meant).
 *
 * ── VERSION DISCIPLINE (AC 7.5 / W18) ─────────────────────────────────────
 * Every mutation carries the newest `version` this view holds (adopted from
 * every read AND every mutation response). A 409 CONFLICT means another
 * device wrote first: the view re-runs the full `GET /api/queue/reconcile`
 * walk (numeric `after`/`nextAfter`, restart on a mid-walk version change)
 * and adopts the server's assembly — it never strips the version and retries,
 * which would clobber the other device's write. Display pages (`getQueue`)
 * are used only to RENDER; the reconcile walk is the only rebuild path.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 9.1's files */

import {
  ERROR_CODES,
  QUEUE_ITEMS_MAX,
  TESTIDS,
  moveReorderButton,
  ordinalTestId,
} from '@streamymusic/shared';

import { ApiError, fieldErrorText } from '../../../api/api-client.js';
import { ActionSheet, ConfirmModal, InputModal } from '../../app/overlays.js';
import { VirtualList } from '../../components/VirtualList.js';
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  RETRIABLE_ERROR_MESSAGE,
} from '../../components/StateBlocks.js';
import { Tab } from '../../components/primitives/Chip.js';
import { append, el } from '../../components/primitives/dom.js';
import { formatSeconds } from '../../components/primitives/format.js';
import { albumThumb } from '../../components/CoverThumb.js';

import { HistoryTab } from './HistoryTab.js';
import { MoveToPositionSheet, moveToPositionDirective } from './MoveToPositionSheet.js';

/** ui-contract §10.6 scale table — /queue renders 60 up-next rows, then +60. */
export const QUEUE_PAGE_SIZE = 60;

/** mock L595 — the up-next empty panel (AC 7.6: renders, never blank). */
export const QUEUE_EMPTY_HEADLINE = "QUEUE'S DRY";
export const QUEUE_EMPTY_MESSAGE =
  'Add tracks with "Add to queue" or "Play next" from any track menu.';

/** ui-contract §10.5 "Clear queue confirmation" — copy verbatim. */
export const CLEAR_QUEUE_TITLE = 'Clear the queue?';
export const CLEAR_QUEUE_BODY =
  'This removes all upcoming tracks. Whatever’s playing keeps playing.';
/** mock `qClear` — the guard toast when there is nothing to clear. */
export const QUEUE_ALREADY_EMPTY_MESSAGE = 'Queue is already empty.';

/** Announced while a 409 sends the view through the reconcile walk. */
export const QUEUE_CONFLICT_MESSAGE = 'The queue changed on another device — refreshing.';

/** mock `_imSubmit` (L933) — the playlist-name rules, verbatim copy. */
export const NAME_REQUIRED_ERROR = 'Name is required (1–100 characters).';
export const NAME_TOO_LONG_ERROR = 'Name must be 100 characters or fewer.';

/** How far from a viewport edge the drag auto-scroll region starts. */
export const DRAG_EDGE_PX = 48;
/** Max px scrolled per tick when the pointer is hard against the edge. */
export const DRAG_MAX_STEP_PX = 24;
/** Auto-scroll tick cadence while a drag is active. */
export const DRAG_SCROLL_INTERVAL_MS = 50;
/** Horizontal travel that commits a swipe-remove (mock fp swipe used 40). */
export const SWIPE_REMOVE_PX = 60;
/** Vertical drift above which a gesture is a scroll, not a swipe. */
export const SWIPE_MAX_DRIFT_PX = 32;

/** Give up re-reconciling after this many mid-walk version changes. */
const RECONCILE_RESTARTS_MAX = 5;

/**
 * The item the queue treats as "currently playing" — the server's
 * `playingIndex` clamp (services/queue.ts), mirrored so both ends point at
 * the same row even when an older client left `currentIndex` past the end.
 *
 * @param {number} currentIndex
 * @param {number} totalItems
 * @returns {number} clamped index, or -1 for an empty queue
 */
export function playingIndexOf(currentIndex, totalItems) {
  if (totalItems <= 0) return -1;
  return Math.min(Math.max(currentIndex, 0), totalItems - 1);
}

/**
 * AC 7.2 — px to scroll this tick for a pointer at `clientY`. Zero outside
 * the edge regions; ramps linearly to ±`maxStep` at the edge itself. Pure, so
 * the reorder suite can pin the ramp without faking layout.
 *
 * @param {number} clientY
 * @param {number} viewportHeight
 * @param {number} [edge]
 * @param {number} [maxStep]
 * @returns {number} negative scrolls up, positive scrolls down
 */
export function autoScrollStep(clientY, viewportHeight, edge = DRAG_EDGE_PX, maxStep = DRAG_MAX_STEP_PX) {
  if (clientY < edge) {
    return -Math.ceil(((edge - Math.max(clientY, 0)) / edge) * maxStep);
  }
  const fromBottom = viewportHeight - clientY;
  if (fromBottom < edge) {
    return Math.ceil(((edge - Math.max(fromBottom, 0)) / edge) * maxStep);
  }
  return 0;
}

/**
 * @typedef {object} QueueViewHandlers
 * @property {(name: string) => void} onSaveQueueAsPlaylist called with the
 *   validated, trimmed name; the composition root owns the actual
 *   `POST /api/playlists/from-queue` call (this view must not import the
 *   playlists surface) and surfaces its outcome
 */

/**
 * Builds the `/queue` section and starts loading whichever tab is active.
 *
 * @param {{
 *   api: Pick<
 *     import('../../../api/api-client.js').ApiClient,
 *     'getQueue'|'mutateQueue'|'reconcileQueue'|'getQueueHistory'|'readdQueueHistory'
 *   >,
 *   heading: HTMLElement,
 *   overlays: import('../../app/OverlayStack.js').OverlayStackHandle,
 *   handlers: QueueViewHandlers,
 *   tab?: 'queue'|'history',
 *   pageSize?: number,
 *   announce?: (text: string) => void,
 *   scroller?: { scrollTop: number },
 *   viewportHeight?: () => number,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` — handed in by
 *   the shell, never created here. `tab` mirrors the mock's `?tab=` query
 *   (the tabs are LINKS; switching is a route change the router re-renders).
 *   `scroller`/`viewportHeight` are the drag auto-scroll seams — production
 *   omits them (document scrolling element / window.innerHeight), jsdom
 *   suites inject them because jsdom performs no layout.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function QueueView(options) {
  const {
    api,
    heading,
    overlays,
    handlers,
    tab = 'queue',
    pageSize = QUEUE_PAGE_SIZE,
    announce,
    scroller = /** @type {{ scrollTop: number }} */ (
      document.scrollingElement ?? document.documentElement
    ),
    viewportHeight = () => window.innerHeight,
  } = options;

  heading.textContent = 'UP NEXT';

  const section = el('section', { attrs: { 'data-screen-label': 'Queue' } });
  const history = tab === 'history';

  const tabs = el('div', {
    className: 'sm-tab-row',
    attrs: { role: 'tablist', 'aria-label': 'Queue sections' },
  });
  append(
    tabs,
    Tab({
      label: 'QUEUE',
      accent: 'pink',
      size: 'md',
      selected: !history,
      href: '#/queue?tab=queue',
      testId: TESTIDS.QUEUE_TAB,
    }),
    Tab({
      label: 'HISTORY',
      accent: 'yellow',
      size: 'md',
      selected: history,
      href: '#/queue?tab=history',
      // ui-contract §3 records BOTH ids for this one control: the /queue crawl
      // derived `history-link` from its accessible name, the history sub-tab
      // crawl promoted `queue-history-tab` for the SELECTED state. Carrying
      // the state-matching id keeps both contract tables satisfiable.
      testId: history ? TESTIDS.QUEUE_HISTORY_TAB : TESTIDS.HISTORY_LINK,
    }),
  );
  append(section, heading, tabs);

  if (history) {
    const historyTab = HistoryTab({
      api,
      ...(announce === undefined ? {} : { announce }),
    });
    section.append(historyTab.element);
    return {
      element: section,
      dispose() {
        historyTab.dispose();
      },
    };
  }

  /* ── queue tab skeleton (built ONCE; only `body` content swaps) ───────── */

  const saveButton = el('button', {
    className: 'sm-btn sm-btn--secondary-13 sm-btn--panel',
    text: 'Save queue as playlist',
    testId: TESTIDS.SAVE_QUEUE_AS_PLAYLIST_BUTTON,
    attrs: { type: 'button', disabled: true },
    on: { click: () => openSaveAsPlaylist() },
  });
  const clearButton = el('button', {
    className: 'sm-btn sm-btn--secondary-13 sm-btn--panel-danger',
    text: 'Clear queue…',
    testId: TESTIDS.CLEAR_QUEUE_BUTTON,
    attrs: { type: 'button' },
    on: { click: () => openClearConfirm() },
  });

  const currentHost = el('div');
  const actions = append(el('div', { className: 'sm-actions' }), saveButton, clearButton);
  const countLabel = el('p', { className: 'sm-count-label', attrs: { hidden: true } });
  const body = el('div');
  /** Mutation failures and failed load-mores land here; loaded rows survive. */
  const notice = el('div');
  append(section, currentHost, actions, countLabel, body, notice);

  /* ── state ────────────────────────────────────────────────────────────── */

  /** Loaded display items, ABSOLUTE server positions. @type {import('@streamymusic/shared').QueueItem[]} */
  let items = [];
  /** @type {string|null} */
  let version = null;
  let currentIndex = 0;
  let totalItems = 0;
  /** @type {string|null} */
  let cursor = null;
  let generation = 0;
  let disposed = false;
  /** @type {AbortController|null} */
  let controller = null;
  /** One mutation at a time — the version token serialises them anyway. */
  let mutating = false;
  /**
   * Keyboard continuity across the repaint a mutation forces: the control to
   * re-focus, keyed by the item it belonged to (testids shift with ordinals,
   * items do not).
   *
   * @type {{ itemId: string, control: 'up'|'down'|'remove'|'more' }|null}
   */
  let pendingFocus = null;
  /** @type {Map<string, { up: HTMLElement, down: HTMLElement, remove: HTMLElement, more: HTMLElement }>} */
  const rowsByItemId = new Map();

  const playing = () => playingIndexOf(currentIndex, totalItems);
  /** Absolute position of up-next display slot 1. */
  const firstUpcomingPosition = () => playing() + 1;
  /** Server-truth up-next count (the loaded slice may be shorter). */
  const upcomingTotal = () => Math.max(totalItems - playing() - 1, 0);
  const upcomingHeld = () => items.filter((item) => item.position > playing());

  /* ── rows ─────────────────────────────────────────────────────────────── */

  /** mock L586 — the 16px three-bar equaliser on the NOW PLAYING card. */
  function Equalizer() {
    const eq = el('span', {
      className: 'sm-eq sm-eq--16',
      attrs: { 'aria-label': 'Now playing' },
    });
    append(
      eq,
      el('span', { className: 'sm-eq-bar' }),
      el('span', { className: 'sm-eq-bar' }),
      el('span', { className: 'sm-eq-bar' }),
    );
    return eq;
  }

  /**
   * mock L584-589 — the NOW PLAYING card. Fed by the SERVER's current item:
   * AC 7.4's "whatever's playing keeps playing" is the server retaining this
   * row through `clear`, and the card reflecting exactly that.
   *
   * @param {import('@streamymusic/shared').QueueItem} item
   * @returns {HTMLElement}
   */
  function NowPlayingCard(item) {
    const card = el('div', { className: 'sm-nowplaying' });
    append(
      card,
      albumThumb({
        id: item.track.albumId,
        title: item.track.albumTitle,
        coverRef: item.track.coverRef,
      }),
      append(
        el('span', { className: 'sm-flex-fill' }),
        el('span', { className: 'sm-nowplaying__eyebrow', text: 'NOW PLAYING' }),
        el('span', { className: 'sm-line-primary', text: item.track.title }),
        el('span', {
          className: 'sm-line-secondary',
          text: `${item.track.artistName} — ${item.track.albumTitle}`,
        }),
      ),
      Equalizer(),
    );
    return card;
  }

  /**
   * mock L598-607 — one up-next row: handle · ordinal · title/sub · duration ·
   * ↑ ↓ ✕ ⋯. The ordinal testids follow the contract's duplicate convention
   * (`remove-from-queue-button`, `-2`, …).
   *
   * @param {import('@streamymusic/shared').QueueItem} item
   * @param {number} index 0-based index in the visible up-next list
   * @returns {HTMLElement}
   */
  function QueueRow(item, index) {
    const ordinal = index + 1;
    const row = el('div', { className: 'sm-track-row sm-track-row--drag sm-track-row--pan-y' });

    const handle = el('span', {
      // `--pointer` sets `touch-action:none` so the drag gesture is never
      // stolen by the browser as a page pan (the row itself keeps `pan-y`).
      className: 'sm-drag-handle sm-drag-handle--pointer',
      text: '≡',
      attrs: { 'aria-hidden': 'true', title: 'Drag to reorder' },
      on: {
        pointerdown: (event) => {
          startDrag(/** @type {PointerEvent} */ (event), item, index, row);
        },
      },
    });

    const up = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-narrow',
      text: '↑',
      testId: moveReorderButton('up', ordinal),
      attrs: { type: 'button', 'aria-label': 'Move up' },
      on: {
        click: () => {
          // Slot 1's "up" would cross ABOVE the playing item (see header).
          if (index === 0) return;
          void mutate(
            { kind: 'reorder', itemId: item.itemId, move: { kind: 'up' } },
            { itemId: item.itemId, control: 'up' },
          );
        },
      },
    });
    const down = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-narrow',
      text: '↓',
      testId: moveReorderButton('down', ordinal),
      attrs: { type: 'button', 'aria-label': 'Move down' },
      on: {
        click: () => {
          void mutate(
            { kind: 'reorder', itemId: item.itemId, move: { kind: 'down' } },
            { itemId: item.itemId, control: 'down' },
          );
        },
      },
    });
    const remove = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-narrow sm-btn--icon-danger',
      text: '✕',
      testId: ordinalTestId(TESTIDS.REMOVE_FROM_QUEUE_BUTTON, ordinal),
      attrs: { type: 'button', 'aria-label': 'Remove from queue' },
      on: {
        click: () => {
          void mutate({ kind: 'remove', itemId: item.itemId });
        },
      },
    });
    const more = el('button', {
      className: 'sm-btn sm-btn--icon sm-btn--icon-narrow sm-btn--icon-narrow-more',
      text: '⋯',
      testId: ordinalTestId(TESTIDS.MORE_QUEUE_ACTIONS_BUTTON, ordinal),
      attrs: { type: 'button', 'aria-label': 'More queue actions' },
      on: { click: () => openMoreActions(item, index) },
    });

    append(
      row,
      handle,
      el('span', {
        className: 'sm-track-row__no sm-track-row__no--12',
        text: String(ordinal),
      }),
      append(
        el('span', { className: 'sm-flex-fill' }),
        el('span', { className: 'sm-line-primary', text: item.track.title }),
        el('span', {
          className: 'sm-line-secondary',
          text: `${item.track.artistName} — ${item.track.albumTitle}`,
        }),
      ),
      el('span', { className: 'sm-line-trailing', text: formatSeconds(item.track.durationS) }),
      up,
      down,
      remove,
      more,
    );

    row.addEventListener('pointerdown', (event) => {
      startSwipe(/** @type {PointerEvent} */ (event), item);
    });

    rowsByItemId.set(item.itemId, { up, down, remove, more });
    return row;
  }

  const list = VirtualList({
    items: [],
    windowSize: pageSize,
    increment: pageSize,
    renderItem: (item, index) => QueueRow(item, index),
    // §10.6 ordered-list affordance, verbatim shape: "Show more (888 more)".
    loadMoreLabel: ({ remaining }) => `Show more (${remaining} more)`,
    loadMoreTestId: TESTIDS.QUEUE_SHOW_MORE_BUTTON,
    listClassName: 'sm-stack-6',
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  /* ── painting ─────────────────────────────────────────────────────────── */

  function paint() {
    const playingAt = playing();
    const current = items.find((item) => item.position === playingAt);
    currentHost.replaceChildren();
    if (current !== undefined) currentHost.append(NowPlayingCard(current));

    if (totalItems === 0) saveButton.setAttribute('disabled', '');
    else saveButton.removeAttribute('disabled');

    countLabel.textContent = `${upcomingTotal().toLocaleString()} upcoming`;
    countLabel.removeAttribute('hidden');

    const upcoming = upcomingHeld();
    if (upcoming.length === 0 && cursor === null) {
      // AC 7.6 — never blank, even with a NOW PLAYING card above.
      body.replaceChildren(
        EmptyBlock({
          headline: QUEUE_EMPTY_HEADLINE,
          message: QUEUE_EMPTY_MESSAGE,
          ...(announce === undefined ? {} : { announce }),
        }),
      );
      restoreFocus();
      return;
    }

    rowsByItemId.clear();
    list.update({ items: upcoming, hasMore: cursor !== null });
    if (body.firstChild !== list.element) body.replaceChildren(list.element);
    restoreFocus();
  }

  /**
   * Puts focus back on the control the last mutation was issued from — the
   * repaint rebuilt every row, and dropping a keyboard user back to `<body>`
   * after each "Move up" would make the accessible path unusable in practice.
   */
  function restoreFocus() {
    if (pendingFocus === null) return;
    const focus = pendingFocus;
    pendingFocus = null;
    const entry = rowsByItemId.get(focus.itemId);
    if (entry === undefined) return;
    entry[focus.control].focus({ preventScroll: true });
  }

  /**
   * @param {import('@streamymusic/shared').QueueResponse} page
   */
  function adopt(page) {
    version = page.version;
    currentIndex = page.currentIndex;
    totalItems = page.totalItems;
  }

  /* ── loading ──────────────────────────────────────────────────────────── */

  /**
   * @param {{ append?: boolean, quiet?: boolean, keep?: number }} [opts]
   *   `append` fetches the next display page onto what is held. `quiet`
   *   refreshes in place after a mutation — no skeleton, rows stay up until
   *   the fresh data lands. `keep` asks the fresh load to cover at least that
   *   many up-next rows so a grown window survives the refresh.
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const quiet = opts.quiet === true;
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage && !quiet) {
      items = [];
      cursor = null;
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading queue' }));
    }

    try {
      if (appendPage) {
        const page = await api.getQueue({
          limit: pageSize,
          ...(cursor === null ? {} : { cursor }),
          signal: ownController.signal,
        });
        if (disposed || own !== generation) return;
        adopt(page);
        items = [...items, ...page.items];
        cursor = page.nextCursor;
      } else {
        const needed = Math.max(opts.keep ?? 0, pageSize);
        /** @type {import('@streamymusic/shared').QueueItem[]} */
        let fresh = [];
        /** @type {string|null} */
        let freshCursor = null;
        // Walk display pages until the up-next window is covered: the pages
        // start at position 0, and `setCurrentIndex` may have left a played
        // prefix before the row the user actually cares about.
        let safety = Math.ceil(QUEUE_ITEMS_MAX / pageSize) + 1;
        do {
          const page = await api.getQueue({
            limit: pageSize,
            ...(freshCursor === null ? {} : { cursor: freshCursor }),
            signal: ownController.signal,
          });
          if (disposed || own !== generation) return;
          adopt(page);
          fresh = [...fresh, ...page.items];
          freshCursor = page.nextCursor;
          safety -= 1;
        } while (
          freshCursor !== null &&
          fresh.filter((item) => item.position > playing()).length < needed &&
          safety > 0
        );
        items = fresh;
        cursor = freshCursor;
      }
      paint();
    } catch {
      if (disposed || own !== generation) return;
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage || quiet) {
        // Loaded rows survive a failed page / refresh (§10.6).
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  /* ── mutations ────────────────────────────────────────────────────────── */

  /** @param {string} text */
  function surfaceActionError(text) {
    notice.replaceChildren(el('p', { className: 'sm-form-error', text, attrs: { role: 'alert' } }));
    if (announce !== undefined) announce(text);
  }

  /**
   * Applies one op with the held version, then refreshes the display read.
   *
   * @param {import('@streamymusic/shared').QueueOp} op
   * @param {{ itemId: string, control: 'up'|'down'|'remove'|'more' }} [focusMemo]
   * @returns {Promise<void>}
   */
  async function mutate(op, focusMemo) {
    if (mutating || disposed) return;
    mutating = true;
    try {
      const result = await api.mutateQueue(op, {
        ...(version === null ? {} : { version }),
      });
      if (disposed) return;
      version = result.version;
      currentIndex = result.currentIndex;
      totalItems = result.totalItems;
      pendingFocus = focusMemo ?? null;
      await load({ quiet: true, keep: Math.max(list.renderedCount(), pageSize) });
    } catch (error) {
      if (disposed) return;
      const code = /** @type {{ code?: string }} */ (error).code;
      if (code === ERROR_CODES.CONFLICT) {
        // Another device wrote first — reconcile and re-render; NEVER retry
        // without the version (that would clobber the winner's write).
        if (announce !== undefined) announce(QUEUE_CONFLICT_MESSAGE);
        await resync();
        return;
      }
      if (code === ERROR_CODES.NOT_FOUND) {
        // The item is not ours any more (removed elsewhere) — refresh.
        surfaceActionError(RETRIABLE_ERROR_MESSAGE);
        await load({ quiet: true, keep: Math.max(list.renderedCount(), pageSize) });
        return;
      }
      // Caps (the 1,000-item queue) are 422s whose actionable text is keyed
      // in `fieldErrors`; `error.message` is the generic envelope copy.
      surfaceActionError(
        fieldErrorText(error) ??
          (error instanceof ApiError ? error.message : RETRIABLE_ERROR_MESSAGE),
      );
    } finally {
      mutating = false;
    }
  }

  /**
   * AC 7.5 — the full reconcile walk: numeric `after`/`nextAfter` chunks,
   * restart on a mid-walk version change, adopt the assembly verbatim.
   *
   * @returns {Promise<void>}
   */
  async function resync() {
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;
    notice.replaceChildren();

    try {
      /** @type {import('@streamymusic/shared').QueueItem[]} */
      let assembled = [];
      /** @type {number|undefined} */
      let after;
      /** @type {string|null} */
      let walkVersion = null;
      let restarts = 0;
      for (;;) {
        const chunk = await api.reconcileQueue({
          ...(after === undefined ? {} : { after }),
          signal: ownController.signal,
        });
        if (disposed || own !== generation) return;
        if (walkVersion !== null && chunk.version !== walkVersion) {
          restarts += 1;
          if (restarts > RECONCILE_RESTARTS_MAX) {
            throw new Error('queue kept changing during reconcile');
          }
          assembled = [];
          after = undefined;
          walkVersion = null;
          continue;
        }
        walkVersion = chunk.version;
        version = chunk.version;
        currentIndex = chunk.currentIndex;
        totalItems = chunk.totalItems;
        assembled = [...assembled, ...chunk.items];
        if (chunk.nextAfter === null) break;
        after = chunk.nextAfter;
      }
      items = assembled;
      cursor = null; // the walk holds the WHOLE queue
      paint();
    } catch {
      if (disposed || own !== generation) return;
      body.replaceChildren(
        ErrorBlock({
          onRetry: () => {
            void resync();
          },
          ...(announce === undefined ? {} : { announce }),
        }),
      );
    }
  }

  /* ── overlays ─────────────────────────────────────────────────────────── */

  function openClearConfirm() {
    if (upcomingTotal() === 0) {
      if (announce !== undefined) announce(QUEUE_ALREADY_EMPTY_MESSAGE);
      return;
    }
    overlays.open(
      ConfirmModal({
        title: CLEAR_QUEUE_TITLE,
        body: CLEAR_QUEUE_BODY,
        confirmLabel: 'Clear',
        onConfirm: () => {
          overlays.closeTop({
            then: () => {
              // AC 7.4: the server's `clear` RETAINS the playing item; this
              // view never touches the transport, so playback continues.
              void mutate({ kind: 'clear' });
            },
          });
        },
        onCancel: () => overlays.closeTop(),
      }),
    );
  }

  function openSaveAsPlaylist() {
    overlays.open(
      InputModal({
        title: 'Save queue as playlist',
        label: 'Playlist name',
        placeholder: 'e.g. Tonight’s Set',
        confirmLabel: 'Save',
        onSubmit: (value) => {
          const name = value.trim();
          // CODE POINTS, not UTF-16 units — "🎸🎸" is 2 characters to a human.
          const length = [...name].length;
          if (length < 1) return NAME_REQUIRED_ERROR;
          if (length > 100) return NAME_TOO_LONG_ERROR;
          overlays.closeTop({
            then: () => {
              handlers.onSaveQueueAsPlaylist(name);
            },
          });
          return null;
        },
        onCancel: () => overlays.closeTop(),
      }),
    );
  }

  /**
   * The per-row ⋯ sheet (ui-contract §10.3 queue reorder sheet) plus the
   * net-new "Move to position…" entry (mock-defect fix 5).
   *
   * @param {import('@streamymusic/shared').QueueItem} item
   * @param {number} index 0-based visible up-next index at open time
   */
  function openMoreActions(item, index) {
    /** §10.3: each item closes the sheet, THEN acts. @param {() => void} run */
    const act = (run) => () => {
      overlays.closeTop({ then: run });
    };
    overlays.open(
      ActionSheet({
        title: item.track.title,
        onDismissRequest: () => overlays.closeTop(),
        items: [
          {
            icon: '↑',
            label: 'Move to top',
            onSelect: act(() => {
              // Top of the UP-NEXT list — NOT the API's `{kind:'top'}`
              // (absolute 0, above the playing item; see the header).
              void mutate(
                {
                  kind: 'reorder',
                  itemId: item.itemId,
                  move: moveToPositionDirective(1, firstUpcomingPosition()),
                },
                { itemId: item.itemId, control: 'more' },
              );
            }),
          },
          {
            icon: '↓',
            label: 'Move to bottom',
            onSelect: act(() => {
              void mutate(
                { kind: 'reorder', itemId: item.itemId, move: { kind: 'bottom' } },
                { itemId: item.itemId, control: 'more' },
              );
            }),
          },
          {
            icon: '#',
            label: 'Move to position…',
            onSelect: act(() => openMoveToPosition(item, index)),
          },
          {
            icon: '✕',
            label: 'Remove',
            color: 'var(--sm-color-pink)',
            onSelect: act(() => {
              void mutate({ kind: 'remove', itemId: item.itemId });
            }),
          },
        ],
      }),
    );
  }

  /**
   * @param {import('@streamymusic/shared').QueueItem} item
   * @param {number} index
   */
  function openMoveToPosition(item, index) {
    overlays.open(
      MoveToPositionSheet({
        title: item.track.title,
        position: index + 1,
        total: upcomingTotal(),
        onSubmit: (displayPosition) => {
          overlays.closeTop({
            then: () => {
              void mutate(
                {
                  kind: 'reorder',
                  itemId: item.itemId,
                  move: moveToPositionDirective(displayPosition, firstUpcomingPosition()),
                },
                { itemId: item.itemId, control: 'more' },
              );
            },
          });
          return null;
        },
        onCancel: () => overlays.closeTop(),
      }),
    );
  }

  /* ── drag reorder (AC 7.2: within the rendered window + edge auto-scroll) */

  /**
   * @type {{
   *   item: import('@streamymusic/shared').QueueItem,
   *   from: number,
   *   y: number,
   *   row: HTMLElement,
   *   timer: number,
   * }|null}
   */
  let drag = null;

  /** Ordered rendered rows — the drag space IS the rendered window. */
  const renderedRows = () => [...body.querySelectorAll('.sm-track-row')];

  /**
   * Where the dragged row would land: the number of OTHER rows whose vertical
   * midpoint sits above the pointer.
   *
   * @param {number} clientY
   * @returns {number} 0-based index in the visible up-next list
   */
  function dropIndexFor(clientY) {
    if (drag === null) return 0;
    let index = 0;
    for (const row of renderedRows()) {
      if (row === drag.row) continue;
      const rect = row.getBoundingClientRect();
      if (clientY > rect.top + rect.height / 2) index += 1;
    }
    return index;
  }

  function paintDropTarget() {
    if (drag === null) return;
    const rows = renderedRows();
    const target = dropIndexFor(drag.y);
    for (const row of rows) row.classList.remove('sm-track-row--drop-target');
    const marked = rows[target];
    if (marked !== undefined && marked !== drag.row) {
      marked.classList.add('sm-track-row--drop-target');
    }
  }

  /**
   * @param {PointerEvent} event
   * @param {import('@streamymusic/shared').QueueItem} item
   * @param {number} index
   * @param {HTMLElement} row
   */
  function startDrag(event, item, index, row) {
    if (drag !== null || mutating || disposed) return;
    if (typeof event.button === 'number' && event.button !== 0) return;
    event.preventDefault();
    drag = {
      item,
      from: index,
      y: event.clientY,
      row,
      timer: window.setInterval(dragTick, DRAG_SCROLL_INTERVAL_MS),
    };
    row.classList.add('sm-track-row--dragging');
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
    document.addEventListener('pointercancel', onDragCancel);
  }

  /** @param {Event} event */
  function onDragMove(event) {
    if (drag === null) return;
    drag.y = /** @type {PointerEvent} */ (event).clientY;
    paintDropTarget();
  }

  /** AC 7.2 — scroll while the pointer holds near a vertical edge. */
  function dragTick() {
    if (drag === null) return;
    const step = autoScrollStep(drag.y, viewportHeight());
    if (step !== 0) {
      scroller.scrollTop += step;
      // Rects moved under the pointer — the target must follow the scroll.
      paintDropTarget();
    }
  }

  function onDragEnd() {
    finishDrag(true);
  }

  function onDragCancel() {
    finishDrag(false);
  }

  /** @param {boolean} commit */
  function finishDrag(commit) {
    const state = drag;
    drag = null;
    if (state === null) return;
    window.clearInterval(state.timer);
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragCancel);
    state.row.classList.remove('sm-track-row--dragging');
    for (const row of renderedRows()) row.classList.remove('sm-track-row--drop-target');
    if (!commit) return;
    drag = state; // dropIndexFor needs the dragged row excluded
    const to = dropIndexFor(state.y);
    drag = null;
    if (to === state.from) return;
    void mutate({
      kind: 'reorder',
      itemId: state.item.itemId,
      move: moveToPositionDirective(to + 1, firstUpcomingPosition()),
    });
  }

  /* ── swipe remove ─────────────────────────────────────────────────────── */

  /**
   * mock-parity gesture: a mostly-horizontal swipe on the row removes it.
   * The row's `touch-action: pan-y` keeps VERTICAL panning native, so a
   * scroll that starts on a row is never stolen (the prior story's swipe
   * bug); only horizontal travel is read here, and never from the buttons or
   * the drag handle.
   *
   * @param {PointerEvent} event
   * @param {import('@streamymusic/shared').QueueItem} item
   */
  function startSwipe(event, item) {
    if (drag !== null || mutating || disposed) return;
    const target = /** @type {HTMLElement|null} */ (event.target);
    if (target !== null && target.closest('button, .sm-drag-handle') !== null) return;
    const startX = event.clientX;
    const startY = event.clientY;

    /** @param {Event} upEvent */
    const onUp = (upEvent) => {
      cleanup();
      const up = /** @type {PointerEvent} */ (upEvent);
      const dx = up.clientX - startX;
      const dy = Math.abs(up.clientY - startY);
      if (Math.abs(dx) >= SWIPE_REMOVE_PX && dy <= SWIPE_MAX_DRIFT_PX) {
        void mutate({ kind: 'remove', itemId: item.itemId });
      }
    };
    const onCancel = () => cleanup();
    function cleanup() {
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    }
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      finishDrag(false);
      controller?.abort();
    },
  };
}
